import { Tooltip } from '../../../../components/Tooltip';
import type { SurveyQuestionnaireSummary } from '../surveyTypes';

type Counts = Pick<SurveyQuestionnaireSummary, 'targetedCount' | 'targetedRespondedCount' | 'outsideTargetCount'>;

/**
 * How many of the employees the survey TARGETS have answered — the exact figure, its share, and a
 * thin bar of the same shape as the Summary's charts (one series, so one hue and no legend; the
 * figure is text beside it, so nothing depends on colour). Responses from outside the target are
 * said apart, so "of the targeted" stays true.
 *
 * ONE component on all three Responses sub-tabs (product call, 12 Oct 2026), so the figure cannot
 * read two ways. `compact` is the left panes' — the same words at the list's own text size.
 */
export function SurveyTurnout({ summary, compact = false }: { summary: Counts; compact?: boolean }) {
  const { targetedCount: targeted, targetedRespondedCount: answered, outsideTargetCount: outside } = summary;
  const share = targeted > 0 ? Math.round((answered / targeted) * 100) : 0;
  const figure = `${answered} of ${targeted} targeted ${targeted === 1 ? 'employee' : 'employees'} answered (${share}%)`;

  return (
    <div className="flex flex-col gap-1.5">
      <p className={`${compact ? 'text-xs' : 'text-sm'} text-slate-600`}>
        <span className="font-mono font-medium tabular-nums text-slate-800">{answered}</span> of{' '}
        <span className="font-mono font-medium tabular-nums text-slate-800">{targeted}</span> targeted{' '}
        {targeted === 1 ? 'employee' : 'employees'} answered{' '}
        <span className="font-mono tabular-nums text-slate-400">({share}%)</span>
      </p>

      <Tooltip label={figure} block>
        <span className="flex h-4 items-center" aria-hidden="true">
          <span className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            {answered > 0 ? (
              <span className="block h-2 rounded-full bg-blue-500" style={{ width: `${share}%`, minWidth: 8 }} />
            ) : null}
          </span>
        </span>
      </Tooltip>

      {outside > 0 ? (
        <p className="text-xs text-slate-500">
          <span className="font-mono tabular-nums">{outside}</span> more {outside === 1 ? 'response is' : 'responses are'}{' '}
          from {outside === 1 ? 'an employee' : 'employees'} outside the target.
        </p>
      ) : null}
    </div>
  );
}
